import { CommonModule } from "@angular/common";
import { ChangeDetectionStrategy, Component, Input, OnChanges, OnInit, SimpleChanges } from "@angular/core";
import { AccountService } from "@gtmhub/accounts/accounts-service";
import { IConfidenceMapping, IConfidenceSettings } from "@gtmhub/core";
import { localize } from "@gtmhub/localization";
import { IGtmhubRootScopeService } from "@gtmhub/models";
import { ConfidenceScale, ConfidenceScaleEnum, ConfidenceType } from "@webapp/configuration/models/configuration.model";
import { LocalizationModule } from "@webapp/localization/localization.module";
import { ConfidenceSettingsService } from "@webapp/okrs/metrics/services/confidence-settings.service";
import { getConfidenceFloatNumber } from "@webapp/okrs/utils/confidence-level.utils";
import { UiTagModule } from "@webapp/ui/tag/tag.module";
import { UiTooltipModule } from "@webapp/ui/tooltip/tooltip.module";

interface IConfidencePreviewRootScopeService extends IGtmhubRootScopeService {
  confidenceScale: ConfidenceScale;
  confidenceSettings: IConfidenceSettings;
}

@Component({
  selector: "confidence-label",
  templateUrl: "./confidence-label.component.html",
  styleUrls: ["./confidence-label.component.less"],
  changeDetection: ChangeDetectionStrategy.OnPush,
  standalone: true,
  imports: [CommonModule, UiTagModule, UiTooltipModule, LocalizationModule],
})
export class ConfidenceLabelComponent implements OnInit, OnChanges {
  @Input() public confidenceValue: number | string;
  @Input() public disableTooltip?: boolean;
  @Input() public currentAttainment?: string | number;
  public confidenceType: ConfidenceType;
  public confidenceName: string;
  public confidenceSettings: IConfidenceSettings;
  public confidenceScale: ConfidenceScale;
  public confidenceLevelTooltip: string;
  public confidenceOverflow: string;
  public mappings: IConfidenceMapping[];
  public color: string;
  public emojiClass: string;
  public $root: IConfidencePreviewRootScopeService;
  public maxConfidenceCharactersLength = 6;

  constructor(private accountService: AccountService, private confidenceService: ConfidenceSettingsService) {}

  public ngOnInit(): void {
    const scaleFactor = 10;
    this.accountService.setConfidenceSettingsToRootScope();
    this.confidenceSettings = this.confidenceService.getConfidenceSettings();
    this.confidenceScale = this.confidenceService.getConfidenceScale();
    this.confidenceType = this.confidenceSettings?.confidenceType ?? "numeric";

    this.confidenceValue = getConfidenceFloatNumber(parseFloat(this.confidenceValue as string), scaleFactor).toString(); // Convert back to string
    this.confidenceType = this.confidenceService.getConfidenceSettings().confidenceType || "numeric";

    const confidenceMappingCopy = this.confidenceSettings.confidenceMapping.map((it) => Object.assign({}, it));
    this.mappings = this.getFloatedMappings(
      confidenceMappingCopy.sort((a: IConfidenceMapping, b: IConfidenceMapping) => a.range.to - b.range.to),
      scaleFactor
    );

    const mapping = this.findRange(parseFloat(this.confidenceValue), this.mappings);
    this.setViewVariables(mapping, parseFloat(this.confidenceValue));
    this.setConfidenceOverflow();
  }

  public ngOnChanges(changes: SimpleChanges): void {
    if (changes.confidenceValue) {
      this.ngOnInit();
    }
  }

  private getFloatedMappings(mappings: IConfidenceMapping[], scaleFactor: number): IConfidenceMapping[] {
    const clonedMappings = mappings.map((m) => ({ ...m }));
    const biggestMappingRange = Math.max(...clonedMappings.map((m) => m.range.to));

    if (biggestMappingRange > 1) {
      clonedMappings.forEach((m) => {
        m.range.to = m.range.to / scaleFactor;
        m.range.from = m.range.from / scaleFactor;
      });
    }

    return clonedMappings;
  }

  private isScaleMinor(confidenceScale: ConfidenceScale): boolean {
    return confidenceScale === ConfidenceScaleEnum.Minor;
  }

  private findRange(value: number, mappings: IConfidenceMapping[]): IConfidenceMapping {
    return mappings.find((m) => m.range.from <= value && value <= m.range.to);
  }

  private setViewVariables(mapping: IConfidenceMapping, confidenceValue: number): void {
    if (!mapping) {
      this.confidenceName = null;
      this.color = null;
      this.emojiClass = null;
      return;
    }

    const confidenceScale = this.accountService.getConfidenceScale();
    const scaleFactor = 10;
    const confidenceTooltipValue = this.isScaleMinor(confidenceScale) ? confidenceValue.toFixed(1) : Math.round(confidenceValue * scaleFactor);

    switch (this.confidenceType) {
      case "numeric":
        this.confidenceName = this.isScaleMinor(confidenceScale) ? confidenceValue.toFixed(1) : Math.round(confidenceValue * scaleFactor) + "/10";
        this.color = mapping.color;
        this.confidenceLevelTooltip = localize("confidence_level_cap");
        break;

      case "text":
        this.confidenceName = mapping.name;
        this.color = mapping.color;
        this.confidenceLevelTooltip = `${localize("confidence_level_cap")} ${confidenceTooltipValue}`;
        break;

      case "emoji":
        this.emojiClass = "twa twa-lg twa-" + mapping.name;
        this.color = null;
        this.confidenceLevelTooltip = `${localize("confidence_level_cap")} ${confidenceTooltipValue}`;
        break;

      default:
        this.confidenceName = null;
        this.color = null;
        this.emojiClass = null;
        break;
    }
  }

  private setConfidenceOverflow(): void {
    if (this.confidenceOverflow && this.currentAttainment) {
      let attainmentValue;
      if (typeof this.currentAttainment === "string") {
        attainmentValue = (parseFloat(this.currentAttainment) * 100).toLocaleString() + "%";
      } else {
        attainmentValue = (this.currentAttainment * 100).toLocaleString() + "%";
      }
      const smallScreenOverflow = window.innerWidth < 980 && (attainmentValue + this.confidenceName).length > 50;
      const bigScreenOverflow = window.innerWidth >= 980 && (attainmentValue + this.confidenceName).length > 20;

      this.confidenceOverflow = smallScreenOverflow || bigScreenOverflow ? "overflow" : "no-overflow";
    }
  }
}
